import { useMenu } from 'react-instantsearch';
import FilterSection from '@/components/skus/FilterSkus/FilterSection';
import FilterItem from '@/components/skus/FilterSkus/FilterItem';
import SpiritoryInput from '@/components/common/Forms/SpiritoryInput';
import DefaultButton from '@/components/common/default/DefaultButton';
import { debounce } from '@/utilites/debounce';
import { AlertCircle, ChevronDown, Minus, Search, X } from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import router from 'next/router';
import {
  createBrandUrl,
  createCategoryUrl,
  createCountyOfOriginUrl,
  createRegionUrl,
} from '@/utilites/search/create-search-urls';
import { usePathname, useSearchParams } from 'next/navigation';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { CommonEnum } from '@/types/translations/CommonEnum';

interface SearchableMenuFilterProps {
  attribute: string;
  title: string;
  icon: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  searchPlaceholder?: string;
  limit?: number;
  showMoreLimit?: number;
  className?: string;
  disableRedirect?: boolean;
  secondaryAccentColor?: string;
}

// Enhanced constants for better maintainability
const DEFAULT_FILTER_LIMITS = {
  INITIAL: 8,
  EXPANDED: 20,
  SEARCH_THRESHOLD: 2,
  DEBOUNCE_DELAY: 300,
} as const;

const SearchableMenuFilter = memo<SearchableMenuFilterProps>(
  ({
    attribute,
    title,
    icon,
    isOpen,
    onToggle,
    searchPlaceholder,
    limit = DEFAULT_FILTER_LIMITS.INITIAL,
    showMoreLimit = DEFAULT_FILTER_LIMITS.EXPANDED,
    className = '',
    disableRedirect = false,
    secondaryAccentColor,
  }) => {
    const { translate } = useTypedTranslation<CommonEnum>('common');
    const { items, refine, canRefine } = useMenu({
      attribute,
      limit: showMoreLimit,
    });
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const [inputValue, setInputValue] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [showMore, setShowMore] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Enhanced debounced search with loading state for local filtering
    const debouncedSearch = useMemo(
      () =>
        debounce((query: string) => {
          setIsSearching(true);
          try {
            setSearchQuery(query);
          } catch (error) {
            console.error('Search error:', error);
          } finally {
            setTimeout(() => setIsSearching(false), 100);
          }
        }, DEFAULT_FILTER_LIMITS.DEBOUNCE_DELAY),
      []
    );

    const handleSearch = useCallback(
      (value: string) => {
        setInputValue(value);
        debouncedSearch(value);
      },
      [debouncedSearch]
    );

    const handleRefine = useCallback(
      (value: string) => {
        try {
          refine(value);
        } catch (error) {
          console.error('Refine error:', error);
        }
      },
      [refine]
    );

    const handleShowMore = useCallback(() => {
      setShowMore(prev => !prev);
    }, []);

    const handleClearSearch = useCallback(() => {
      setInputValue('');
      setSearchQuery('');
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, []);

    // Enhanced memoized computations
    const hasActiveRefinements = useMemo(() => items.some(item => item.isRefined), [items]);

    const displayItems = useMemo(() => {
      const filteredItems = searchQuery
        ? items.filter(item => item.label.toLowerCase().includes(searchQuery.toLowerCase()))
        : items;

      // Sort items: refined items first, then non-refined items
      const sortedItems = [...filteredItems].sort((a, b) => {
        // If one is refined and the other isn't, refined comes first
        if (a.isRefined && !b.isRefined) return -1;
        if (!a.isRefined && b.isRefined) return 1;
        // If both have same refinement status, maintain original order
        return 0;
      });

      return showMore ? sortedItems : sortedItems.slice(0, limit);
    }, [items, searchQuery, showMore, limit]);

    const memoizedItems = useMemo(
      () =>
        displayItems.map(item => (
          <FilterItem
            key={item.value}
            label={item.label}
            count={item.count}
            isSelected={item.isRefined}
            onClick={() => {
              // Skip redirects if disableRedirect is true
              if (disableRedirect) {
                return handleRefine(item.value);
              }

              if (attribute === 'brand.name' && pathname.includes('/brands/') === false) {
                return router.push(createBrandUrl(item.value, searchParams));
              } else if (
                attribute === 'brand.countryOfOrigin' &&
                pathname.includes('/country/') === false
              ) {
                return router.push(createCountyOfOriginUrl(item.value, searchParams));
              } else if (
                attribute === 'category.name' &&
                pathname.includes('/category/') === false
              ) {
                return router.push(createCategoryUrl(item.value, searchParams));
              } else if (attribute === 'brand.region' && pathname.includes('/region/') === false) {
                return router.push(createRegionUrl(item.value, searchParams));
              }

              return handleRefine(item.value);
            }}
            disabled={!canRefine}
            aria-label={`${item.isRefined ? translate(CommonEnum.remove_labelVariableName_filter_parenthesis_itemCountVariable_items_parenthesis).replace('{{labelVariableName}}', item.label).replace('{{itemCountVariable}}', item.count.toString()) : translate(CommonEnum.add_labelVariableName_filter_parenthesis_itemCountVariable_items_parenthesis).replace('{{labelVariableName}}', item.label).replace('{{itemCountVariable}}', item.count.toString())}`}
          />
        )),
      [
        displayItems,
        handleRefine,
        canRefine,
        disableRedirect,
        attribute,
        pathname,
        searchParams,
        translate,
      ]
    );

    const showMoreControls = useMemo(() => {
      const filteredItemsCount = searchQuery
        ? items.filter(item => item.label.toLowerCase().includes(searchQuery.toLowerCase())).length
        : items.length;

      const canShowMore = !searchQuery && filteredItemsCount > limit && !showMore;
      const canShowLess = !searchQuery && showMore;

      return { canShowMore, canShowLess, filteredItemsCount };
    }, [searchQuery, items, limit, showMore]);

    // Focus management
    useEffect(() => {
      if (isOpen && searchInputRef.current) {
        const timer = setTimeout(() => {
          searchInputRef.current?.focus();
        }, 100);
        return () => clearTimeout(timer);
      }
    }, [isOpen]);

    // Cancel debouncedSearch on unmount
    useEffect(() => {
      return () => {
        debouncedSearch.cancel();
      };
    }, [debouncedSearch]);

    return (
      <FilterSection
        id={attribute}
        title={title}
        icon={icon}
        isOpen={isOpen}
        onToggle={onToggle}
        hasActiveFilters={hasActiveRefinements}
        isLoading={isSearching}
        className={className}
        secondaryAccentColor={secondaryAccentColor}
      >
        <div className="tw-space-y-2">
          <div className="tw-relative tw-px-3">
            <SpiritoryInput
              ref={searchInputRef}
              type="search"
              value={inputValue}
              placeholder={searchPlaceholder || `Search ${title.toLowerCase()}...`}
              className="tw-w-full tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-py-2.5 tw-pl-10 tw-text-sm tw-text-gray-900 tw-placeholder-gray-500 tw-transition-all tw-duration-200 focus:tw-border-gray-400 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-gray-400"
              onChange={e => handleSearch(e.target.value)}
              disabled={!canRefine}
              hideNativeClear={true}
              aria-label={`${translate(CommonEnum.search)} ${title.toLowerCase()}`}
              aria-describedby={`${attribute}-search-help`}
              rightIcon={
                searchQuery && (
                  <DefaultButton
                    type="button"
                    onClick={handleClearSearch}
                    className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-full tw-p-0 tw-text-gray-400 tw-transition-colors hover:tw-bg-gray-100 hover:tw-text-gray-600 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-400"
                    aria-label="Clear search"
                  >
                    <X className="tw-h-3.5 tw-w-3.5" />
                  </DefaultButton>
                )
              }
              leftIcon={<Search className="tw-h-4 tw-w-4" />}
            />
          </div>
          <div id={`${attribute}-search-help`} className="tw-sr-only">
            {translate(CommonEnum.type_to_search_through_titleVariable_options).replace(
              '{{title}}',
              title.toLowerCase()
            )}
          </div>

          <div
            className="tw-scrollbar-thin tw-scrollbar-track-gray-100 tw-scrollbar-thumb-gray-300 tw-max-h-48 tw-space-y-0.5 tw-overflow-y-auto tw-px-3"
            role="listbox"
            aria-label={`${title} ${translate(CommonEnum.options) || 'options'}`}
          >
            {memoizedItems.length > 0 ? (
              memoizedItems
            ) : (
              <div className="tw-flex tw-items-center tw-gap-2 tw-p-3 tw-text-sm tw-text-gray-500">
                <AlertCircle className="tw-h-4 tw-w-4" />
                <span>
                  {searchQuery
                    ? translate(CommonEnum.no_results_found_for_search_query).replace(
                        '{{searchQuery}}',
                        searchQuery
                      )
                    : translate(CommonEnum.no_options_available) || 'No options available'}
                </span>
              </div>
            )}
          </div>

          {(showMoreControls.canShowMore || showMoreControls.canShowLess) && (
            <div className="tw-border-t tw-border-gray-100 tw-pt-2">
              <DefaultButton
                type="button"
                onClick={handleShowMore}
                className="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-md tw-border tw-border-gray-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-transition-colors hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-400 focus:tw-ring-offset-2"
                aria-label={
                  showMore
                    ? translate(CommonEnum.show_fewer_options) || 'Show fewer options'
                    : translate(CommonEnum.show_more_options) || 'Show more options'
                }
              >
                {showMore ? (
                  <>
                    <Minus className="tw-h-4 tw-w-4" />
                    {translate(CommonEnum.show_less) || 'Show Less'}
                  </>
                ) : (
                  <>
                    <ChevronDown className="tw-h-4 tw-w-4" />
                    {translate(CommonEnum.show_more) || 'Show More'} (
                    {showMoreControls.filteredItemsCount - limit} more)
                  </>
                )}
              </DefaultButton>
            </div>
          )}
        </div>
      </FilterSection>
    );
  }
);

SearchableMenuFilter.displayName = 'SearchableMenuFilter';

export default SearchableMenuFilter;
